/* GrowthOS — Call modal for the lead record (softphone overlay).
   Ported pixel-for-pixel from design growthos/Modals.CallModal: Calling… → Connected
   with a live timer, mute toggle, end-call + close, lead detail rows, and a
   "Fill Disposition" action. This is a simulated call surface (the app has no
   click-to-call telephony backend); it shows the lead's real contact details and
   the End / Close / Fill Disposition actions dismiss it. */
import { useEffect, useState } from 'react';
import { Ic } from "../../shared/component/GosIcon";


const AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const initials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
const avaColor = (name) =>
  AVA_COLORS[
    String(name || '')
      .split('')
      .reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length
  ];

const LeadCallModal = ({ lead = {}, onClose }) => {
  const [phase, setPhase] = useState('calling'); // calling -> connected
  const [secs, setSecs] = useState(0);
  const [muted, setMuted] = useState(false);

  const fullName = lead.fullName || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Lead';

  useEffect(() => {
    const t = setTimeout(() => setPhase('connected'), 2600);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (phase !== 'connected') return undefined;
    const i = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(i);
  }, [phase]);

  const connected = phase === 'connected';
  const mm = String(Math.floor(secs / 60)).padStart(2, '0');
  const ss = String(secs % 60).padStart(2, '0');

  const rows = [
    ['user', 'Name', fullName],
    ['mail', 'Email', lead.email || '—'],
    ['phone', 'Phone', lead.phone || lead.mobile || '—'],
    ['location', 'Region', lead.state || '—'],
    ['globe', 'Timezone', lead.timeZone || '—'],
  ];

  return (
    <div className="gos-import-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={'gos-call-modal' + (connected ? ' connected' : '')} role="dialog" aria-modal="true" aria-label="Call">
        {/* call bar */}
        <div className="gos-call-bar">
          <Ic name="grid" size={18} className="gos-call-grid" />
          <Ic name="phone" size={17} className={connected ? 'gos-call-ph-on' : 'gos-call-ph'} />
          <span className={'gos-call-status' + (connected ? ' on' : '')}>
            {connected ? 'Connected' : 'Calling…'}
          </span>
          <div className="gos-call-spacer" />
          <button type="button" className={'gos-call-round' + (muted ? ' muted-on' : '')} onClick={() => setMuted((m) => !m)} title={muted ? 'Unmute' : 'Mute'}>
            <Ic name={muted ? 'micOff' : 'mic'} size={16} />
          </button>
          <span className="gos-call-timer">{connected ? `${mm}:${ss}` : '00:00'}</span>
          <button type="button" className="gos-call-end" onClick={onClose} title="End call">
            <Ic name="phone" size={17} style={{ transform: 'rotate(135deg)' }} />
          </button>
          <button type="button" className="gos-lr-iconbtn gos-lr-iconbtn-sm" onClick={onClose} aria-label="Close">
            <Ic name="x" size={17} />
          </button>
        </div>

        {/* lead header card */}
        <div className="gos-call-card gos-call-lead">
          <span className="gos-call-ava" style={{ background: avaColor(fullName) }}>{initials(fullName)}</span>
          <div className="gos-call-lead-meta">
            <div className="gos-call-name">{fullName}</div>
            {lead.companyName ? <div className="gos-call-company">{lead.companyName}</div> : null}
          </div>
        </div>

        {/* detail rows */}
        <div className="gos-call-card gos-call-rows">
          {rows.map((r, i) => (
            <div className="gos-lr-drow" key={i}>
              <div className="gos-lr-dlabel">
                <Ic name={r[0]} size={15} /> {r[1]}
              </div>
              <div className={'gos-lr-dval' + (r[2] === '—' ? ' muted' : '')}>{r[2]}</div>
            </div>
          ))}
        </div>

        {/* disposition */}
        <div className="gos-call-foot">
          <button type="button" className="gos-call-dispo" onClick={onClose}>
            <Ic name="edit" size={16} /> Fill Disposition
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadCallModal;
